import store from '@/state/store'
import axios from 'axios'
import router from './router'
import { alertError } from '@/helpers/swal'

const api = axios.create({
  baseURL: window.apiUrl,
})

// Store active AbortControllers
let controllers = []

export function abortPendingRequests() {
  controllers.forEach((controller) => controller.abort())
  controllers = []
}

// Attach Token and AbortController
api.interceptors.request.use(
  (config) => {
    const token = store.state.auth.user?.accessToken
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Prefer caller-provided AbortSignal; otherwise attach one for route-change abort.
    if (!config.signal) {
      const controller = new AbortController()
      config.signal = controller.signal
      controllers.push(controller)
    }

    return config
  },
  (error) => Promise.reject(error)
)

// Handle 401 Unauthorized and Cleanup
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Remove controller for this request
    if (error.config?.signal) {
      controllers = controllers.filter((c) => c.signal !== error.config.signal)
    }

    const isCancelled =
      axios.isCancel(error) ||
      error?.code === 'ERR_CANCELED' ||
      error?.name === 'CanceledError' ||
      error?.cancelled === true

    if (isCancelled) {
      return Promise.reject({ cancelled: true })
    }

    if (error.config?.skipErrorAlert) {
      return Promise.reject(error)
    }

    if (error.response) {
      const status = error.response.status

      if (status === 401) {
        return handleUnauthorized('Token expired or invalid')
      }

      if (status === 400 && error.response.data?.errors) {
        return Promise.reject({ validationErrors: error.response.data.errors })
      }

      const errorMessage =
        error.response.data?.message ||
        error.response.data?.error ||
        'Something went wrong. Please try again.'

      alertError(errorMessage)
    } else {
      alertError('Network error. Please check your connection.')
    }

    return Promise.reject(error)
  }
)

function handleUnauthorized(message) {
  if (router.currentRoute.path !== '/auth/login') {
    store.dispatch('auth/authLogout')
    window.location.href = '/auth/login'
  }
  return Promise.reject(message)
}

export default api
